import gql from 'graphql-tag';
import { apolloClient as apollo } from '@/vue-apollo';
import _ from 'lodash';
import { isNil } from '@/shared/utils';
import { PROJECT_CARBON_FIELD, FIELDS_BUDGET_USER_EXPENSE_SHEET, BUDGET_FILTERS_STORE, BUDGET_LAST_FOCUSED_STORE } from '@/shared/constants';
import router from '@/router';

const columnExpense = `
	id
	userProjectId
	encoderId
	date
	type
	description
	kgCoTwo
	amount
	amountVat
	amountTotal
	comment
	validated
	departmentName
	functionName
	encoderId
	encoder {
		id
		name
		firstName
		fullName
	}
	user {
		id
		name
		firstName
	}				
`;

export const computeBudget = async (projectId = null) => {
	try {
		const RECOMPUTE_BUDGET = gql`
			query {
				RecomputeBudget(ProjectId: null)
			}
		`;

		const {
			data: { GetBudgetDataList }
		} = await apollo.query({
			query: RECOMPUTE_BUDGET,
			fetchPolicy: 'no-cache'
		});

		return GetBudgetDataList;
	} catch (error) {
		console.log(error);
	}
};

export const getProjectUserSalaries = async (filter = {}, fetchPolicy = 'no-cache') => {
	try {
		let QUERY_GET_PROJECT_USER_SALARIES = gql`
			query GetProjectUserSalaries($filter: AdvancedGlobalFilterInput) {
				GetProjectUserSalaries(Filter: $filter) {
					id
					status
					startDate
					endDate
					fullName
					departmentName
					functionName
					hours
					salary
					co2
					hoursStr
					departmentValue
					category
					categoryName
					categoryCostCenter
					timesheet {
						id
						processed
						jsError
						jsLog
					}
				}
			}
		`;

		const {
			data: { GetProjectUserSalaries }
		} = await apollo.query({
			query: QUERY_GET_PROJECT_USER_SALARIES,
			variables: {
				filter
			},
			fetchPolicy: fetchPolicy
		});

		return GetProjectUserSalaries;
	} catch (error) {
		console.log(error);
	}
};

const QUERY_GET_PROJECT_USER_EXPENSE_DETAIL = gql`
	query GetProjectUserExpenseDetail($filter: AdvancedGlobalFilterInput) {
		GetProjectUserExpenseDetail(Filter: $filter) {
			expense {
				${columnExpense}
			}
			expenseItem {
				category
				paiementType
				supplierName
				supplierId
				type
				subType
				additionalSubType
				department
				departmentName
				userDepartmentName
				description
				subCategory
				date
				id
				images
				amount
				amountTotal
				amountVat
				comment
				km
				kmRate
				kgCoTwo
				fromLocation
				toLocation
				validated
				categoryName
				functionName
				paiementTypeName
				costCenter
				encoderId
				function
				carbon {
					${PROJECT_CARBON_FIELD}
				}
				processed
				encoder {
					id
					name
					email
					firstName
					fullName
				}
				user {
					id
					name
					firstName
				}
				projectFlagItems {
					taxeFlags {
						id
						flagId
						value
						name
						short
						factor
						amount
						payCode
						color
						allowanceRate
						bookmark
						category
						digitalSubmission
						disabled
						costCenter
						forScreen
						fieldType
						defaultValue
						jsFormula
					}
					customFlags {
						id
						flagId
						value
						name
						short
						factor
						amount
						payCode
						color
						allowanceRate
						bookmark
						category
						digitalSubmission
						disabled
						costCenter
						forScreen
						fieldType
						defaultValue
						jsFormula
					}
				}
			}
		}
	}
`;

export const getProjectUserExpenseDetail = async (filter = {}, fetchPolicy = 'no-cache') => {
	try {
		const {
			data: { GetProjectUserExpenseDetail }
		} = await apollo.query({
			query: QUERY_GET_PROJECT_USER_EXPENSE_DETAIL,
			variables: {
				filter
			},
			fetchPolicy: fetchPolicy
		});
		return GetProjectUserExpenseDetail;
	} catch (error) {
		console.log(error);
		return [];
	}
};

export const updateProjectUserExpenseDetailCache = async (cache, isDelete = false) => {
	// To run only when it concern buget GL expenses details.
	if (router.currentRoute.path.includes('budget') && router.currentRoute.query.activeTabExpenseItem) {
		const subjectID = +localStorage.getItem(BUDGET_LAST_FOCUSED_STORE.EXPENSEITEMS);
		if (isDelete) {
			// shortcut solution refresh entire cache.
			cache.reset();
			// updatedData = cacheData.GetProjectUserExpenseDetail.filter((data) => +data.expenseItem.id !== subjectID);
		} else {
			try {
				// read query cache.
				const cacheFilter = { filter: JSON.parse(localStorage.getItem(BUDGET_FILTERS_STORE.EXPENSEITEMS)) };
				const cacheData = cache.readQuery({ query: QUERY_GET_PROJECT_USER_EXPENSE_DETAIL, variables: cacheFilter });
				// get matching updated data.
				const matchingUpdated = await apollo.query({
					query: QUERY_GET_PROJECT_USER_EXPENSE_DETAIL,
					variables: {
						filter: {
							subjectId: subjectID
						}
					},
					fetchPolicy: 'network-only'
				});
				// match update.
				const updatedData =
					matchingUpdated.data.GetProjectUserExpenseDetail.length > 0
						? cacheData.GetProjectUserExpenseDetail.map((data) => {
								if (data.expenseItem.id === matchingUpdated.data.GetProjectUserExpenseDetail[0].expenseItem.id) {
									return matchingUpdated.data.GetProjectUserExpenseDetail[0];
								} else {
									return data;
								}
						  })
						: cacheData.GetProjectUserExpenseDetail;
				// Re-write cahce data.
				cache.writeQuery({
					query: QUERY_GET_PROJECT_USER_EXPENSE_DETAIL,
					variables: cacheFilter,
					data: { GetProjectUserExpenseDetail: updatedData }
				});
			} catch (error) {
				if (error.name && error.name === 'Invariant Violation') {
					// that mean, the fetch policy of the current query is not 'cache-first';
					return;
				}
			}
		}
	}
};

const QUERY_GET_PROJECT_USER_PO_DETAIL = gql`
	query GetProjectUserPoDetail($filter: AdvancedGlobalFilterInput) {
		GetProjectUserPoDetail(Filter: $filter) {
			purchaseOrder {
				id
				userProjectId
				encoderId
				poReference
				description
				department
				supplierId
				consumed
				requestDate
				deliveryDate
				deliveryAddress
				invoicedToProduction
				validated
				laValidatedId
				laValidatedWhen
				lbValidatedId
				lbValidatedWhen
				amount
				amountVat
				amountTotal
				remainingAmount
				information
				comment
				notDetailed
				departmentName
				supplierName
				function
				functionName
				expenseTotal
				validatedMsg
				projectTitle
				projectIdEx
				isInContract
				images
				itemCategory
				processed
				user {
					id
					name
					firstName
				}
				encoder {
					id
					name
					firstName
				}
			}
		}
	}
`;

export const getProjectUserPoDetail = async (filter = {}, fetchPolicy = 'no-cache') => {
	try {
		const {
			data: { GetProjectUserPoDetail }
		} = await apollo.query({
			query: QUERY_GET_PROJECT_USER_PO_DETAIL,
			variables: {
				filter
			},
			fetchPolicy: fetchPolicy
		});

		return GetProjectUserPoDetail;
	} catch (error) {
		console.log(error);
		return [];
	}
};

export const updateProjectUserPoDetailCache = async (cache, isDelete) => {
	// To run only when it concern buget GL Purchases orders.
	if (router.currentRoute.path.includes('budget')) {
		if (isDelete) {
			// shortcut solution refresh entire cache.
			cache.reset();
		} else {
			try {
				// get matching updated data.
				const matchingUpdated = await apollo.query({
					query: QUERY_GET_PROJECT_USER_PO_DETAIL,
					variables: {
						filter: {
							subjectId: +router.currentRoute.params.id,
							groupBySheets: true
						}
					},
					fetchPolicy: 'network-only'
				});
				// read query cache.
				const cacheFilter = { filter: JSON.parse(localStorage.getItem(BUDGET_FILTERS_STORE.PURCHASE_ORDERS)) };
				const cacheData = cache.readQuery({ query: QUERY_GET_PROJECT_USER_PO_DETAIL, variables: cacheFilter });

				const updatedData = cacheData.GetProjectUserPoDetail.map((data) => {
					if (+data.purchaseOrder.id === +matchingUpdated.data.GetProjectUserPoDetail[0].purchaseOrder.id) {
						return matchingUpdated.data.GetProjectUserPoDetail[0];
					} else {
						return data;
					}
				});
				// Re-write cahce data.
				cache.writeQuery({
					query: QUERY_GET_PROJECT_USER_PO_DETAIL,
					variables: cacheFilter,
					data: { GetProjectUserPoDetail: updatedData }
				});
			} catch (error) {
				if (error.name && error.name === 'Invariant Violation') {
					// that mean, the fetch policy of the current query is not 'cache-first';
					return;
				}
			}
		}
	}
};

export const assignDocumentPackageToUser = async (documentId = 0, userId = 0, ContractId = null, oldDocId = null) => {
	try {
		const mutation = gql`
			mutation ($documentId: ID!, $userId: ID!, $ContractId: ID!, $oldDocId: ID) {
				AssignOnBoardingPackage(DocumentId: $documentId, UserId: $userId, ContractId: $ContractId, OldDocID: $oldDocId)
			}
		`;

		let variables = {
			documentId: parseInt(documentId, 10),
			userId: parseInt(userId, 10),
			ContractId: parseInt(ContractId, 10)
		};

		if (!isNil(oldDocId) && !isNaN(oldDocId)) {
			variables = {
				...variables,
				oldDocId: parseInt(oldDocId, 10)
			};
		}

		const {
			data: { AssignOnBoardingPackage }
		} = await apollo.mutate({
			mutation,
			variables,
			fetchPolicy: 'no-cache'
		});

		return AssignOnBoardingPackage;
	} catch (e) {
		console.error({ e });
	}
};

export const getBudgetHierarchicalCategoryJson = async (
	department = 0,
	useCategoryId = false,
	contentType = null,
	co2Category = -1,
	UseAppChoice = false,
	IsGrouping = false,
	ProjectId = null
) => {
	try {
		// contentType => -1 (all), 0 (expense), 1 (salary)
		const query = gql`
			query (
				$department: ID!
				$useCategoryId: Boolean!
				$contentType: ID
				$co2Category: ID
				$UseAppChoice: Boolean
				$IsGrouping: Boolean
				$ProjectId: ID
			) {
				GetBudgetDetailHierarchicalListAsJsonString(
					Department: $department
					UseCategoryId: $useCategoryId
					ContentType: $contentType
					Co2Category: $co2Category
					UseAppChoice: $UseAppChoice
					IsGrouping: $IsGrouping
					ProjectId: $ProjectId
				)
			}
		`;

		const {
			data: { GetBudgetDetailHierarchicalListAsJsonString }
		} = await apollo.query({
			query,
			variables: {
				department: parseInt(department, 10),
				useCategoryId,
				contentType,
				co2Category,
				UseAppChoice,
				IsGrouping,
				ProjectId
			},
			fetchPolicy: 'no-cache'
		});

		return JSON.parse(GetBudgetDetailHierarchicalListAsJsonString);
	} catch (error) {
		// console.log(error)
	}
};

export const getBudgetHierarchicalAsJSON = async (startDate, endDate, departmentId = -1, forceCompute = false) => {
	try {
		let QUERY_GET_BUDGET_HIERARCHICAL_AS_JSON = null;
		let variables = null;

		if (!_.isNil(departmentId)) {
			QUERY_GET_BUDGET_HIERARCHICAL_AS_JSON = gql`
				query ($startDate: String, $endDate: String, $departmentId: ID, $forceCompute: Boolean) {
					GetBudgetDataHierarchicalListAsJsonString(
						DepartmentNumber: $departmentId
						StrTime: $startDate
						EndTime: $endDate
						ForceCompute: $forceCompute
					)
				}
			`;

			variables = {
				startDate,
				endDate,
				departmentId: parseInt(departmentId),
				forceCompute
			};
		} else {
			QUERY_GET_BUDGET_HIERARCHICAL_AS_JSON = gql`
				query ($startDate: String, $endDate: String, $forceCompute: Boolean) {
					GetBudgetDataHierarchicalListAsJsonString(StrTime: $startDate, EndTime: $endDate, ForceCompute: $forceCompute)
				}
			`;

			variables = {
				startDate,
				endDate,
				forceCompute
			};
		}

		const {
			data: { GetBudgetDataHierarchicalListAsJsonString }
		} = await apollo.query({
			query: QUERY_GET_BUDGET_HIERARCHICAL_AS_JSON,
			variables,
			fetchPolicy: 'no-cache'
		});

		return GetBudgetDataHierarchicalListAsJsonString;
	} catch (error) {
		console.log(error);
	}
};

export const getTotalRecordsBudgetSalaries = async (filter = {}, fetchPolicy = 'no-cache') => {
	try {
		const QUERY_GET_TOTAL_RECORDS_BUDGET_SALARIES = gql`
			query ($filter: AdvancedGlobalFilterInput) {
				GetProjectUserSalariesTotalRecords(Filter: $filter)
			}
		`;

		const {
			data: { GetProjectUserSalariesTotalRecords }
		} = await apollo.query({
			query: QUERY_GET_TOTAL_RECORDS_BUDGET_SALARIES,
			variables: {
				filter
			},
			fetchPolicy: fetchPolicy
		});

		return GetProjectUserSalariesTotalRecords;
	} catch (error) {
		console.log(error);
	}
};

export const getTotalRecordsBudgetExpenses = async (filter = {}, fetchPolicy = 'no-cache') => {
	try {
		const QUERY_GET_TOTAL_RECORDS_BUDGET_EXPENSES = gql`
			query GetProjectUserExpenseDetailTotalRecords($filter: AdvancedGlobalFilterInput) {
				GetProjectUserExpenseDetailTotalRecords(Filter: $filter)
			}
		`;

		const {
			data: { GetProjectUserExpenseDetailTotalRecords }
		} = await apollo.query({
			query: QUERY_GET_TOTAL_RECORDS_BUDGET_EXPENSES,
			variables: {
				filter
			},
			fetchPolicy: fetchPolicy
		});

		return GetProjectUserExpenseDetailTotalRecords;
	} catch (error) {
		console.log(error);
		return 0;
	}
};

export const getTotalRecordsBudgetPO = async (filter = {}, fetchPolicy = 'no-cache') => {
	try {
		const QUERY_GET_TOTAL_RECORDS_BUDGET_PO = gql`
			query GetProjectUserPoDetailTotalRecords($filter: AdvancedGlobalFilterInput) {
				GetProjectUserPoDetailTotalRecords(Filter: $filter)
			}
		`;

		const {
			data: { GetProjectUserPoDetailTotalRecords }
		} = await apollo.query({
			query: QUERY_GET_TOTAL_RECORDS_BUDGET_PO,
			variables: {
				filter
			},
			fetchPolicy: fetchPolicy
		});

		return GetProjectUserPoDetailTotalRecords;
	} catch (error) {
		console.log(error);
		return 0;
	}
};

const QUERY_GET_PROJECT_USER_EXPENSE_SHEET = gql`
	query GetProjectUserExpenseSheet($filter: AdvancedGlobalFilterInput) {
		GetProjectUserExpenseSheet(Filter: $filter) {
			${FIELDS_BUDGET_USER_EXPENSE_SHEET}
		}
	}
`;

export const getProjectUserExpenseSheet = async (filter = {}, fetchPolicy = 'no-cache') => {
	try {
		const {
			data: { GetProjectUserExpenseSheet }
		} = await apollo.query({
			query: QUERY_GET_PROJECT_USER_EXPENSE_SHEET,
			variables: {
				filter
			},
			fetchPolicy: fetchPolicy
		});
		return GetProjectUserExpenseSheet;
	} catch (e) {
		console.error(e);
		return [];
	}
};

const QUERY_GET_TOTAL_RECORDS_EXPENSE_SHEET = gql`
	query GetProjectUserExpenseSheetTotalRecords($filter: AdvancedGlobalFilterInput) {
		GetProjectUserExpenseSheetTotalRecords(Filter: $filter)
	}
`;

export const updateProjectUserExpenseSheetCache = async (cache, isDelete = false) => {
	// To run only when it concern buget GL expensesheets.
	if (router.currentRoute.query.fromBudget || (router.currentRoute.path.includes('budget') && router.currentRoute.query.activeTabExpenseSheet)) {
		const subjectID = router.currentRoute.params.id ? +router.currentRoute.params.id : +localStorage.getItem(BUDGET_LAST_FOCUSED_STORE.EXPENSESHEETS);
		// get matching updated data.
		const matchingUpdated = await apollo.query({
			query: QUERY_GET_PROJECT_USER_EXPENSE_SHEET,
			variables: {
				filter: {
					subjectId: subjectID
				}
			},
			fetchPolicy: 'network-only'
		});
		if (matchingUpdated.data.GetProjectUserExpenseSheet.length > 0) {
			// read query cache.
			try {
				const cacheFilter = { filter: JSON.parse(localStorage.getItem(BUDGET_FILTERS_STORE.EXPENSESHEETS)) };
				const cacheData = cache.readQuery({ query: QUERY_GET_PROJECT_USER_EXPENSE_SHEET, variables: cacheFilter });

				const updatedData = cacheData.GetProjectUserExpenseSheet.map((data) => {
					if (data.expense.id === matchingUpdated.data.GetProjectUserExpenseSheet[0].expense.id) {
						return matchingUpdated.data.GetProjectUserExpenseSheet[0];
					} else {
						return data;
					}
				});
				// Re-write cahce data.
				cache.writeQuery({
					query: QUERY_GET_PROJECT_USER_EXPENSE_SHEET,
					variables: cacheFilter,
					data: { GetProjectUserExpenseSheet: updatedData }
				});
			} catch (error) {
				if (error.name && error.name === 'Invariant Violation') {
					// that mean, the fetch policy of the current query is not 'cache-first';
					return;
				}
			}
		} else if (matchingUpdated.data.GetProjectUserExpenseSheet.length === 0 && isDelete) {
			// shortcut solution refresh entire cache.
			cache.reset();
		}
	}
};

export const getTotalRecordsExpenseSheet = async (filter = {}, fetchPolicy = 'no-cache') => {
	try {
		const {
			data: { GetProjectUserExpenseSheetTotalRecords }
		} = await apollo.query({
			query: QUERY_GET_TOTAL_RECORDS_EXPENSE_SHEET,
			variables: {
				filter
			},
			fetchPolicy: fetchPolicy
		});

		return GetProjectUserExpenseSheetTotalRecords;
	} catch (error) {
		console.log(error);
		return 0;
	}
};

export const SendPurchaseOrdersSlice = async (PurchaseOrderId) => {
	try {
		const mutation = gql`
			mutation ($PurchaseOrderId: [ID]!) {
				SendPurchaseOrdersSlice(PurchaseOrderId: $PurchaseOrderId)
			}
		`;

		let variables = {
			PurchaseOrderId
		};

		const {
			data: { SendPurchaseOrdersSlice }
		} = await apollo.mutate({
			mutation,
			variables,
			fetchPolicy: 'no-cache'
		});

		return SendPurchaseOrdersSlice;
	} catch (e) {
		console.error({ e });
	}
};

/**
 *
 * @param idOfComponent
 * @param type (0: Salaries, 1: Expense sheet, 2: Expense, 3: PO)
 * @param valueOfProcessToChange
 * @returns {Promise<void>}
 */
export const changeStatusOfProcess = async (idOfComponent, type, valueOfProcessToChange) => {
	try {
		const mutation = gql`
			mutation ($idOfComponent: ID!, $type: Int!, $valueOfProcessToChange: Boolean!) {
				ChangeStatusOfProcess(IdOfComponent: $idOfComponent, Type: $type, ValueOfProcessToChange: $valueOfProcessToChange)
			}
		`;

		const {
			data: { ChangeStatusOfProcess }
		} = await apollo.mutate({
			mutation,
			variables: {
				idOfComponent: +idOfComponent,
				type,
				valueOfProcessToChange
			},
			fetchPolicy: 'no-cache'
		});

		return ChangeStatusOfProcess;
	} catch (e) {
		console.log({ ERROR_CHANGE_STATUS_OF_PROCESS: e });
	}
};

export const getBudgetDataDetailList = async () => {
	const {
		data: { GetBudgetDataDetailList }
	} = await apollo.query({
		query: gql`
			query {
				GetBudgetDataDetailList {
					department
					category
					amountTotal
					amountLeft
					purchaseOrdersTotal
				}
			}
		`,
		fetchPolicy: 'no-cache'
	});

	return GetBudgetDataDetailList;
};
